import { Fragment, useId, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import { PipelineLoan } from '../types';
import {
  BreakdownRow,
  LOAN_TYPE_LABELS,
  PURPOSE_LABELS,
  CHANNEL_LABELS,
  RATE_TYPE_LABELS,
  getLoanTypeCategory,
  getPurposeCategory,
  getChannelCategory,
  getRateTypeCategory,
  getStateCategory,
  getStateLabels,
  buildBreakdown,
  buildNonQmSubBreakdown,
  formatCurrency,
  formatPct
} from './fundedSummaryModel';

interface FundedSummaryReportProps {
  loans: PipelineLoan[];
  isLoading: boolean;
}

// --- Reusable breakdown table ---

export function BreakdownTable({
  title,
  dimensionLabel,
  rows,
  subRows
}: {
  title: string;
  dimensionLabel: string;
  rows: BreakdownRow[];
  // Optional nested rows keyed by parent row label. Their pct is share of the parent subset
  // (e.g. share of Non-QM), so they are display-only and excluded from the TOTAL below.
  subRows?: Record<string, BreakdownRow[]>;
}) {
  const titleId = useId();
  const totalVolume = rows.reduce((sum, r) => sum + r.volume, 0);
  const totalUnits = rows.reduce((sum, r) => sum + r.units, 0);

  return (
    <Card className="min-w-0">
      <CardHeader className="pb-3">
        <CardTitle id={titleId} className="text-base">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <Table
          aria-labelledby={titleId}
          containerProps={{
            tabIndex: 0,
            role: 'region',
            'aria-labelledby': titleId,
            className: 'rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring'
          }}
        >
          <TableHeader>
            <TableRow>
              <TableHead scope="col" className="w-[240px]">{dimensionLabel}</TableHead>
              <TableHead scope="col" className="text-right">Volume ($)</TableHead>
              <TableHead scope="col" className="text-right">Volume (Units)</TableHead>
              <TableHead scope="col" className="text-right">% by Dollar Volume</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row) => {
              const children = subRows?.[row.label];
              const showChildren =
                !!children && children.length > 0 && (row.units > 0 || row.volume > 0);
              return (
                <Fragment key={row.label}>
                  <TableRow>
                    <TableCell>{row.label}</TableCell>
                    <TableCell className="text-right">{formatCurrency(row.volume)}</TableCell>
                    <TableCell className="text-right">{row.units}</TableCell>
                    <TableCell className="text-right">{formatPct(row.pct)}</TableCell>
                  </TableRow>
                  {showChildren &&
                    children.map((sub) => (
                      <TableRow key={`${row.label}::${sub.label}`} className="bg-muted/30">
                        <TableCell className="pl-8 text-sm text-muted-foreground">
                          — {sub.label}
                        </TableCell>
                        <TableCell className="text-right text-sm text-muted-foreground">
                          {formatCurrency(sub.volume)}
                        </TableCell>
                        <TableCell className="text-right text-sm text-muted-foreground">
                          {sub.units}
                        </TableCell>
                        <TableCell
                          className="text-right text-sm text-muted-foreground"
                          title="Share of Non-QM volume"
                        >
                          {formatPct(sub.pct)}
                        </TableCell>
                      </TableRow>
                    ))}
                </Fragment>
              );
            })}
          </TableBody>
          <TableFooter>
            <TableRow>
              <TableCell className="font-bold">TOTAL</TableCell>
              <TableCell className="text-right font-bold">{formatCurrency(totalVolume)}</TableCell>
              <TableCell className="text-right font-bold">{totalUnits}</TableCell>
              <TableCell className="text-right font-bold">{formatPct(totalVolume > 0 ? 100 : 0)}</TableCell>
            </TableRow>
          </TableFooter>
        </Table>
      </CardContent>
    </Card>
  );
}

// --- Main component ---

export function FundedSummaryReport({ loans, isLoading }: FundedSummaryReportProps) {
  const loanTypeRows = useMemo(
    () => buildBreakdown(loans, getLoanTypeCategory, [...LOAN_TYPE_LABELS]),
    [loans]
  );

  const nonQmSubtypeRows = useMemo(() => buildNonQmSubBreakdown(loans), [loans]);

  const purposeRows = useMemo(
    () => buildBreakdown(loans, getPurposeCategory, [...PURPOSE_LABELS]),
    [loans]
  );

  const stateRows = useMemo(
    () => buildBreakdown(loans, getStateCategory, getStateLabels(loans)),
    [loans]
  );

  const channelRows = useMemo(
    () => buildBreakdown(loans, getChannelCategory, [...CHANNEL_LABELS]),
    [loans]
  );

  const rateTypeRows = useMemo(
    () => buildBreakdown(loans, getRateTypeCategory, [...RATE_TYPE_LABELS]),
    [loans]
  );

  const investorRows = useMemo(() => {
    const map = new Map<string, number>();
    loans.forEach((loan) => {
      const inv = (loan.investor_name || '').trim() || 'Other';
      map.set(inv, (map.get(inv) || 0) + (loan.trans_details_loan_amt || 0));
    });
    const sortedLabels = [...map.entries()]
      .sort((a, b) => b[1] - a[1])
      .map(([name]) => name);
    return buildBreakdown(
      loans,
      (loan) => (loan.investor_name || '').trim() || 'Other',
      sortedLabels
    );
  }, [loans]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (loans.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        No funded loans found for the selected criteria.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid items-start gap-6 md:grid-cols-2">
        <BreakdownTable
          title="Loan Type"
          dimensionLabel="Loan Type"
          rows={loanTypeRows}
          subRows={{ 'Non-QM': nonQmSubtypeRows }}
        />
        <div className="min-w-0 space-y-6">
          <BreakdownTable title="Purpose" dimensionLabel="Purpose" rows={purposeRows} />
          <BreakdownTable title="Volume by State" dimensionLabel="State" rows={stateRows} />
        </div>
        <BreakdownTable title="Origination Channel" dimensionLabel="Channel" rows={channelRows} />
        <BreakdownTable title="Fixed / ARM" dimensionLabel="Rate Type" rows={rateTypeRows} />
      </div>
      <BreakdownTable title="Volume by Investor" dimensionLabel="Investor" rows={investorRows} />
    </div>
  );
}
